/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { TypedEventEmitter } from "@fluid-internal/client-utils";
import { assert } from "@fluidframework/core-utils/internal";
import { create404Response } from "@fluidframework/runtime-utils/internal";
/**
 * This is a bare-bones base class that does basic setup and enables for factory on an initialize call.
 * You probably don't want to inherit from this data store directly unless
 * you are creating another base data store class
 *
 * @typeParam I - The optional input types used to strongly type the data object
 * @legacy
 * @alpha
 */
export class PureDataObject extends TypedEventEmitter {
    get id() {
        return this.runtime.id;
    }
    /**
     * {@inheritDoc @fluidframework/core-interfaces#IProvideFluidLoadable.IFluidLoadable}
     */
    get IFluidLoadable() {
        return this;
    }
    /**
     * {@inheritDoc @fluidframework/core-interfaces#IProvideFluidHandle.IFluidHandle}
     */
    get IFluidHandle() {
        return this.handle;
    }
    /**
     * Handle to a data store
     */
    get handle() {
        // PureDataObjectFactory already provides an entryPoint initialization function to the data store runtime,
        // so this object should always have access to a non-null entryPoint. Need to cast because PureDataObject
        // tried to be too smart with its typing for handles :).
        assert(this.runtime.entryPoint !== undefined, 0x46b /* EntryPoint was undefined */);
        return this.runtime.entryPoint;
    }
    static async getDataObject(runtime) {
        const obj = await runtime.entryPoint.get();
        return obj;
    }
    constructor(props) {
        super();
        this.runtime = props.runtime;
        this.context = props.context;
        this.providers = props.providers;
        this.initProps = props.initProps;
    }
    /**
     * Return this object if someone requests it directly
     * We will return this object in two scenarios:
     *
     * 1. the request url is a "/"
     *
     * 2. the request url is empty
     */
    async request(req) {
        return req.url === "" || req.url === "/" || req.url.startsWith("/?")
            ? { mimeType: "fluid/object", status: 200, value: this }
            : create404Response(req);
    }
    /**
     * Call this API to ensure PureDataObject is fully initialized.
     * Initialization happens on demand, only on as-needed bases.
     * In most cases you should allow factory/object to decide when to finish initialization.
     * But if you are supplying your own implementation of DataStoreRuntime factory and overriding some methods
     * and need a fully initialized object, then you can call this API to ensure object is fully initialized.
     */
    async finishInitialization(existing) {
        if (this.initializeP !== undefined) {
            return this.initializeP;
        }
        this.initializeP = this.initializeInternal(existing);
        return this.initializeP;
    }
    /**
     * Internal initialize implementation. Overwriting this will change the flow of the PureDataObject and should
     * generally not be done.
     *
     * Calls initializingFirstTime, initializingFromExisting, and hasInitialized. Caller is
     * responsible for ensuring this is only invoked once.
     */
    async initializeInternal(existing) {
        await this.preInitialize();
        if (existing) {
            assert(this.initProps === undefined, 0x0be /* "Trying to initialize from existing while initProps is set!" */);
            await this.initializingFromExisting();
        }
        else {
            await this.initializingFirstTime(this.context.createProps ?? this.initProps);
        }
        await this.hasInitialized();
    }
    /**
     * Called every time the data store is initialized, before initializingFirstTime or
     * initializingFromExisting is called.
     */
    async preInitialize() { }
    /**
     * Called the first time the data store is initialized (new creations with a new
     * data store runtime)
     *
     * @param props - Optional props to be passed in on create
     */
    async initializingFirstTime(props) { }
    /**
     * Called every time but the first time the data store is initialized (creations
     * with an existing data store runtime)
     */
    async initializingFromExisting() { }
    /**
     * Called every time the data store is initialized after create or existing.
     */
    async hasInitialized() { }
}
//# sourceMappingURL=pureDataObject.js.map